/**
 * Antwort von `GET /api/v1/version` — Build-Kennung des laufenden Backends.
 *
 * `version === null` heisst „unbekannt" (ENV `APP_VERSION` nicht gesetzt, z. B.
 * lokal). Dann wird NICHT verglichen — siehe DeployVersionService.
 */
export interface DeployVersion {
  version: string | null;
}

/**
 * Werte, die als „eigene Build-Kennung unbekannt" gelten und einen Vergleich
 * ausschliessen:
 *
 * - `dev`            — lokale Entwicklung (so steht es in `environment.ts`)
 * - `__BUILD_ID__`   — Platzhalter aus `environment.prod.ts`, den der
 *                      Image-Build ersetzt. Steht er noch drin, hat der Build
 *                      ihn NICHT gesetzt; dann darf kein Hinweis erscheinen,
 *                      sonst zeigt er dauerhaft an. Der Diagnose-Check meldet
 *                      diesen Fall separat als Warnung.
 * - Leerstring       — defensiv.
 */
export const UNBEKANNTE_BUILD_IDS: readonly string[] = ['', 'dev', '__BUILD_ID__'];

/**
 * Der Vergleich als REINE Funktion — bewusst nicht im Service.
 *
 * Grund: Im Test ist `environment.buildId` immer `'dev'`, der Service wuerde
 * dort also nie einen Unterschied melden und seine Kernlogik bliebe
 * ungetestet. Ein Mock des environment-Moduls waere der Umweg; eine reine
 * Funktion ist der direkte Weg — sie laesst sich mit allen Kombinationen
 * pruefen, ohne Angular, HTTP oder Modul-Mocks.
 *
 * Konservativ by design: Nur wenn BEIDE Kennungen bekannt sind UND sich
 * unterscheiden, gilt ein Update als verfuegbar. Alles andere ist `false`.
 * Ein Banner, das faelschlich dauerhaft anzeigt, wird ignoriert und macht den
 * Mechanismus wertlos.
 */
export function istUpdateVerfuegbar(eigene: string, deployed: string | null): boolean {
  if (deployed === null) return false;
  if (UNBEKANNTE_BUILD_IDS.includes(eigene)) return false;
  if (UNBEKANNTE_BUILD_IDS.includes(deployed)) return false;
  return eigene !== deployed;
}

/**
 * Haengt einen Cache-Buster an eine URL, ohne Pfad, Query oder Fragment zu
 * verlieren.
 *
 * Warum nicht einfach `location.reload()`: Der Browser darf dabei die alte
 * `index.html` aus dem Cache erneut liefern — also genau die Datei, deren
 * veraltete Chunk-Verweise das Problem sind. Ein veraenderter Query-Parameter
 * erzwingt einen frischen Dokument-Request.
 *
 * Als reine Funktion, weil `window.location` in jsdom nicht ersetzbar ist: So
 * ist die URL-Bildung testbar, ohne das location-Objekt zu faelschen. Der
 * Aufruf von `location.replace()` selbst ist trivial und bleibt ungetestet.
 *
 * `stempel` wird injiziert statt intern erzeugt — Tests brauchen einen festen
 * Wert.
 */
export function mitCacheBuster(href: string, stempel: number): string {
  const url = new URL(href);
  url.searchParams.set('_v', String(stempel));
  return url.toString();
}

/**
 * sessionStorage-Schluessel fuer die Kennung, die ein Reload-Versuch erreichen
 * wollte. Bewusst `sessionStorage`: Der Marker gilt fuer diesen Tab und diese
 * Sitzung — ein neuer Tab soll unbelastet starten.
 */
export const RELOAD_ZIEL_KEY = 'pa_update_reload_ziel';

/**
 * Erkennt einen Reload, der nichts gebracht hat.
 *
 * WARUM (Meldung 2026-08-10): Ein Nutzer klickte mehrfach auf „Jetzt neu laden",
 * der Hinweis blieb stehen, erst ein harter Reload half. Aus Sicht des Nutzers
 * war der Knopf kaputt — und der wiederholte, unveraenderte Hinweis gab ihm
 * keinen Anhaltspunkt, was er sonst tun soll. Ein Hinweis, der eine wirkungslose
 * Aufforderung wiederholt, ist schlimmer als keiner: Er behauptet, das Problem
 * sei mit einem Klick behebbar.
 *
 * Erkennung: Vor dem Reload merken wir uns die Kennung, die das Backend gerade
 * meldet. Steht nach dem Neustart derselbe Unterschied gegen GENAU DIESE Kennung
 * noch an, hat der Reload den Stand nicht geaendert. Dann zeigt das Banner einen
 * anderen Text (siehe Komponente) — meist laeuft dann noch ein Deploy, waehrend
 * dessen Backend und Frontend fuer kurze Zeit auseinanderliegen.
 *
 * Kein Zeitstempel-Vergleich: Meldet das Backend inzwischen eine DRITTE Kennung,
 * ist das ein neuer, ungeprueter Fall — dann greift wieder der normale Text.
 */
export function reloadWarWirkungslos(
  gemerktesZiel: string | null,
  deployed: string | null,
  updateVerfuegbar: boolean,
): boolean {
  if (!updateVerfuegbar) return false;
  if (gemerktesZiel === null || deployed === null) return false;
  return gemerktesZiel === deployed;
}

/**
 * Leert alles, was dem Browser einen alten Frontend-Stand liefern kann, bevor
 * neu navigiert wird.
 *
 * WARUM ueberhaupt (Meldung 2026-08-10): Ein veraenderter Query-Parameter
 * erzwingt nur ein frisches DOKUMENT. Er raeumt nichts weg, was schon im Cache
 * Storage oder in einem Service Worker liegt — und `location.reload(true)` gibt
 * es nicht mehr, das Flag wird von allen Browsern ignoriert. Nur ein manueller
 * Hard-Reload (Strg+Shift+R) umgeht bisher wirklich alles. Diese Funktion holt
 * das nach, soweit es einer Seite ohne Nutzergeste moeglich ist.
 *
 * HEUTE registriert die App KEINEN Service Worker (kein `@angular/service-worker`
 * im Projekt) — die beiden Schritte sind also aktuell wirkungslos und bewusst
 * defensiv: Sobald jemand eine PWA-Schicht ergaenzt, wird genau sie zur
 * naechsten Quelle veralteter Chunks, und dann greift das hier ohne weiteres
 * Zutun.
 *
 * Abhaengigkeiten werden HEREINGEGEBEN statt aus `window` gelesen: `caches` und
 * `navigator.serviceWorker` existieren in jsdom nicht, und beide sind nur in
 * sicheren Kontexten (https/localhost) ueberhaupt vorhanden. So ist die Logik
 * testbar, ohne Globals zu faelschen.
 *
 * Wirft NIE: Ein Fehler beim Aufraeumen darf den Reload nicht verhindern — der
 * Reload ist der Zweck, das Aufraeumen nur die Absicherung.
 */
export async function browserCachesLeeren(
  cacheStorage: Pick<CacheStorage, 'keys' | 'delete'> | undefined,
  swContainer: Pick<ServiceWorkerContainer, 'getRegistrations'> | undefined,
): Promise<void> {
  try {
    if (cacheStorage) {
      const namen = await cacheStorage.keys();
      await Promise.all(namen.map((name) => cacheStorage.delete(name)));
    }
  } catch {
    /* bewusst ignoriert — siehe oben */
  }

  try {
    if (swContainer) {
      const registrierungen = await swContainer.getRegistrations();
      await Promise.all(registrierungen.map((r) => r.unregister()));
    }
  } catch {
    /* bewusst ignoriert — siehe oben */
  }
}
